import React, { useCallback, useEffect, useRef, useState } from 'react';

const INACTIVITY_LIMIT_MS = 18 * 60 * 1000; // 18 minutos sin actividad
const WARNING_DURATION_MS = 2 * 60 * 1000;  // 2 minutos de aviso antes de cerrar sesión
const ACTIVITY_EVENTS = ['click', 'keydown', 'mousemove', 'scroll'];

// Última actividad compartida entre las pestañas del navegador (localStorage):
// trabajar en una pestaña mantiene vivas las demás, así una pestaña olvidada
// no cierra la sesión (que es la misma para todas) mientras se trabaja en otra.
const CLAVE_ACTIVIDAD = 'ultima_actividad';
const ESCRITURA_MIN_MS = 5000; // no escribir en localStorage en cada mousemove

const leerActividad = () => {
  try { return Number(localStorage.getItem(CLAVE_ACTIVIDAD)) || 0; } catch { return 0; }
};
const escribirActividad = (t) => {
  try { localStorage.setItem(CLAVE_ACTIVIDAD, String(t)); } catch { /* sin localStorage: solo cuenta esta pestaña */ }
};

export default function InactivityLogout({ onLogout }) {
  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(WARNING_DURATION_MS / 1000);

  const inactivityTimer = useRef(null);
  const warningTimer = useRef(null);
  const countdownInterval = useRef(null);
  const showWarningRef = useRef(false);
  const ultimaLocal = useRef(Date.now()); // actividad de esta pestaña
  const ultimaEscritura = useRef(0);

  const clearAllTimers = () => {
    clearTimeout(inactivityTimer.current);
    clearTimeout(warningTimer.current);
    clearInterval(countdownInterval.current);
  };

  // La más reciente entre esta pestaña y las demás.
  const ultimaActividad = () => Math.max(ultimaLocal.current, leerActividad());

  const registrarActividad = (forzar) => {
    const ahora = Date.now();
    ultimaLocal.current = ahora;
    if (forzar || ahora - ultimaEscritura.current >= ESCRITURA_MIN_MS) {
      ultimaEscritura.current = ahora;
      escribirActividad(ahora);
    }
  };

  const doLogout = useCallback(() => {
    clearAllTimers();
    showWarningRef.current = false;
    setShowWarning(false);
    onLogout();
  }, [onLogout]);

  // Programa la revisión para cuando se cumplirían 18 min desde la última
  // actividad (de cualquier pestaña); si al revisar hubo actividad, reprograma.
  const programarRevision = useCallback(() => {
    clearAllTimers();
    const restante = ultimaActividad() + INACTIVITY_LIMIT_MS - Date.now();
    inactivityTimer.current = setTimeout(() => {
      if (Date.now() - ultimaActividad() < INACTIVITY_LIMIT_MS) programarRevision();
      // eslint-disable-next-line no-use-before-define
      else startWarning();
    }, Math.max(restante, 1000));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startWarning = useCallback(() => {
    showWarningRef.current = true;
    setShowWarning(true);
    setSecondsLeft(WARNING_DURATION_MS / 1000);
    countdownInterval.current = setInterval(() => {
      setSecondsLeft((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    warningTimer.current = setTimeout(() => {
      // Última revisión: si en otra pestaña se trabajó durante el aviso, no se cierra.
      if (Date.now() - ultimaActividad() < INACTIVITY_LIMIT_MS) {
        showWarningRef.current = false;
        setShowWarning(false);
        programarRevision();
      } else {
        doLogout();
      }
    }, WARNING_DURATION_MS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doLogout, programarRevision]);

  const resetTimer = useCallback(() => {
    registrarActividad(true);
    showWarningRef.current = false;
    setShowWarning(false);
    programarRevision();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programarRevision]);

  useEffect(() => {
    const handleActivity = () => {
      // Mientras el modal de aviso está visible, solo el botón "Seguir conectado" reinicia el contador.
      if (!showWarningRef.current) registrarActividad(false);
    };
    // Actividad en otra pestaña: si esta mostraba el aviso, se quita.
    const handleStorage = (e) => {
      if (e.key === CLAVE_ACTIVIDAD && showWarningRef.current
          && Date.now() - ultimaActividad() < INACTIVITY_LIMIT_MS) {
        showWarningRef.current = false;
        setShowWarning(false);
        programarRevision();
      }
    };
    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, handleActivity));
    window.addEventListener('storage', handleStorage);
    resetTimer();
    return () => {
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, handleActivity));
      window.removeEventListener('storage', handleStorage);
      clearAllTimers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!showWarning) return null;

  const minutos = Math.floor(secondsLeft / 60);
  const segundos = secondsLeft % 60;

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <h3 style={styles.title}>Tu sesión está por cerrar por inactividad</h3>
        <p style={styles.countdown}>
          {minutos}:{String(segundos).padStart(2, '0')}
        </p>
        <button style={styles.button} onClick={resetTimer}>
          Seguir conectado
        </button>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: 8,
    padding: 32,
    maxWidth: 400,
    width: '90%',
    textAlign: 'center',
    boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
    fontFamily: 'Tahoma',
  },
  title: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1a3a6e',
    marginBottom: 16,
  },
  countdown: {
    fontSize: 32,
    fontWeight: 'bold',
    color: '#dc2626',
    marginBottom: 24,
  },
  button: {
    padding: '10px 24px',
    border: 'none',
    borderRadius: 8,
    backgroundColor: '#1a3a6e',
    color: 'white',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
};
